import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, FileText, Zap, Settings, Package, X, User, Users, Briefcase, TrendingUp, Building2, BarChart3, LayoutDashboard, UserCircle } from 'lucide-react';
import { useObjects } from '../../services/queries/objectQueries';
import { useWorkflowRules } from '../../services/queries/workflowQueries';
import { getMockRoles, getDefaultSearchPermissions } from '../../services/queries/roleQueries';
import { useCurrentUser as usePermissionCurrentUser } from '../../services/queries/permissionQueries';
import { mergeSearchPermissions } from '../../services/rbac/rolePermissionSummary';
import { persistedClientRecords } from '../../services/mockData/clients';
import { persistedContactRecords } from '../../services/mockData/contacts';
import { persistedLeadRecords } from '../../services/mockData/leads';
import { persistedOpportunityRecords } from '../../services/mockData/opportunities';
import { useGobodoRecords } from '../../services/queries/gobodoRecordQueries';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { useSwitchUser } from '../../contexts/SwitchUserContext';

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  category: 'objects' | 'workflows' | 'fields' | 'settings' | 'reports' | 'dashboards' | 'profile' | 'clients' | 'contacts' | 'leads' | 'opportunities' | 'cases' | 'firms';
  icon: React.ElementType;
  action: () => void;
}

interface GlobalSearchProps {
  onNavigate?: (view: string, params?: Record<string, string>) => void;
  userRole?: string;
}

// Map userRole string to Role object from mockRoles
const getRolesByUserRoleString = (userRoleString?: string) => {
  if (!userRoleString) return [];
  const mockRoles = getMockRoles();
  const normalized = userRoleString.split(',').map((value) => value.trim()).filter(Boolean);

  const roleNameMap: Record<string, string> = {
    'system_admin': 'System Administrator',
    'manager': 'Operations Manager',
    'cb_supervisor': 'Credit Block Team Lead',
    'cb_team': 'Credit Block Processor',
    'cs_supervisor': 'CyberSweep Team Lead',
    'cs_auditor': 'CyberSweep Auditor',
    'data_analyst': 'Data Analyst',
    'employee': 'Data Analyst',
  };

  const matches = [];
  for (const token of normalized) {
    const roleName = roleNameMap[token] || token;
    const role = mockRoles.find((item) => item.name === roleName);
    if (role) matches.push(role);
  }
  return matches;
};

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ onNavigate, userRole }) => {
  const { data: permissionUser } = usePermissionCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Only fetch search data when the modal is open so we don't hit the backend on every page load
  const { data: objects } = useObjects(undefined, { enabled: isOpen });
  const { data: clientWorkflows } = useWorkflowRules('Client__c', { enabled: isOpen });

  const shouldGobodo = shouldUseGobodo();
  const { data: gobodoClients } = useGobodoRecords('Client__c', { enabled: isOpen });
  const { data: gobodoContacts } = useGobodoRecords('Contact__c', { enabled: isOpen });
  const { data: gobodoLeads } = useGobodoRecords('Lead__c', { enabled: isOpen });
  const { data: gobodoOpportunities } = useGobodoRecords('Opportunity__c', { enabled: isOpen });

  // Get the effective role (respects "Login as User" impersonation)
  const { getEffectiveRole, isSwitched, currentUser } = useSwitchUser();
  const effectiveRole = useMemo(() => {
    // If user is switched, use the switched user's role
    if (isSwitched && currentUser) {
      return currentUser.role;
    }
    // Otherwise use the passed userRole
    return userRole;
  }, [isSwitched, currentUser, userRole]);

  const effectiveRoles = useMemo(() => {
    if (isSwitched && currentUser) {
      return getRolesByUserRoleString(currentUser.role);
    }
    if (permissionUser?.roles?.length) {
      return permissionUser.roles;
    }
    return getRolesByUserRoleString(effectiveRole);
  }, [effectiveRole, isSwitched, currentUser, permissionUser]);
  const searchPerms = effectiveRoles.length > 0
    ? mergeSearchPermissions(effectiveRoles)
    : getDefaultSearchPermissions('Data Analyst');

  // Extract permissions with defaults
  const canSearchObjects = searchPerms?.canSearchObjects ?? false;
  const canSearchFields = searchPerms?.canSearchFields ?? false;
  const canSearchWorkflows = searchPerms?.canSearchWorkflows ?? false;
  const canSearchSystemSettings = searchPerms?.canSearchSystemSettings ?? false;
  const canSearchSecurity = searchPerms?.canSearchSecurity ?? false;
  const canSearchReports = searchPerms?.canSearchReports ?? false;
  const canSearchDashboards = searchPerms?.canSearchDashboards ?? false;
  const canSearchProfileSettings = searchPerms?.canSearchProfileSettings ?? true; // Default true for all users
  const canSearchClients = searchPerms?.canSearchClients ?? false;
  const canSearchContacts = searchPerms?.canSearchContacts ?? false;
  const canSearchLeads = searchPerms?.canSearchLeads ?? false;
  const canSearchOpportunities = searchPerms?.canSearchOpportunities ?? false;
  const visibleCategories = searchPerms?.visibleCategories ?? [];

  // Keyboard shortcut: Cmd+K or Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }

      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setQuery('');
        setSelectedIndex(0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Build search results based on role's search permissions
  const allResults: SearchResult[] = [];

  // Objects - show custom objects and core objects (but not system objects)
  // Only show if role has permission
  if (canSearchObjects && visibleCategories.includes('objects')) {
    objects?.filter(obj => obj.category !== 'system').forEach(obj => {
      allResults.push({
        id: `object-${obj.id}`,
        title: obj.label,
        subtitle: obj.description || `Manage ${obj.label.toLowerCase()} records`,
        category: 'objects',
        icon: Package,
        action: () => {
          if (onNavigate) {
            onNavigate('system-admin');
          }
          setIsOpen(false);
        }
      });

      // Add a fields result for each object (navigates to fields tab)
      // Only show if role has permission to search fields
      if (canSearchFields && visibleCategories.includes('fields')) {
        allResults.push({
          id: `field-${obj.id}`,
          title: `${obj.label} Fields`,
          subtitle: `View and manage ${obj.label} fields`,
          category: 'fields',
          icon: FileText,
          action: () => {
            if (onNavigate) {
              onNavigate('system-admin');
            }
            setIsOpen(false);
          }
        });
      }
    });
  }

  // Workflows - only show if role has permission
  if (canSearchWorkflows && visibleCategories.includes('workflows')) {
    clientWorkflows?.forEach(workflow => {
      allResults.push({
        id: `workflow-${workflow.id}`,
        title: workflow.name,
        subtitle: workflow.description || 'Workflow rule',
        category: 'workflows',
        icon: Zap,
        action: () => {
          if (onNavigate) {
            onNavigate('system-admin');
          }
          setIsOpen(false);
        }
      });
    });
  }

  // Settings - only show if role has permission
  if (canSearchSystemSettings && visibleCategories.includes('settings')) {
    const settingsPages = [
      { id: 'security-access', name: 'Security & Access', description: 'Manage permissions and field security', requiresSecurity: true },
      { id: 'workflow-rules', name: 'Workflow Rules', description: 'Automate business processes', requiresSecurity: false },
      { id: 'object-manager', name: 'Object Manager', description: 'Manage custom objects and fields', requiresSecurity: false },
      { id: 'users', name: 'Users', description: 'Manage user accounts', requiresSecurity: true },
      { id: 'roles', name: 'Roles & Permissions', description: 'Configure user roles', requiresSecurity: true },
      { id: 'email-templates', name: 'Email Templates', description: 'Manage email templates', requiresSecurity: false }
    ];

    // Filter out security-related pages if user doesn't have canSearchSecurity
    settingsPages
      .filter(page => !page.requiresSecurity || canSearchSecurity)
      .forEach(page => {
        allResults.push({
          id: `setting-${page.id}`,
          title: page.name,
          subtitle: page.description,
          category: 'settings',
          icon: Settings,
          action: () => {
            if (onNavigate) {
              onNavigate('system-admin');
            }
            setIsOpen(false);
          }
        });
      });
  }

  // Reports - only for managers+
  if (canSearchReports && visibleCategories.includes('reports')) {
    const reports = [
      { id: 'sales-pipeline', name: 'Sales Pipeline Report', description: 'View sales funnel and opportunities' },
      { id: 'lead-conversion', name: 'Lead Conversion Report', description: 'Track lead to opportunity conversion' },
      { id: 'activity-report', name: 'Activity Report', description: 'Team activity metrics' },
      { id: 'revenue-forecast', name: 'Revenue Forecast', description: 'Projected revenue by quarter' },
    ];

    reports.forEach(report => {
      allResults.push({
        id: `report-${report.id}`,
        title: report.name,
        subtitle: report.description,
        category: 'reports',
        icon: BarChart3,
        action: () => {
          if (onNavigate) {
            onNavigate('reports', { reportId: report.id });
          }
          setIsOpen(false);
        }
      });
    });
  }

  // Dashboards - only for managers+
  if (canSearchDashboards && visibleCategories.includes('dashboards')) {
    const dashboards = [
      { id: 'sales-dashboard', name: 'Sales Dashboard', description: 'Sales team performance overview' },
      { id: 'cs-dashboard', name: 'Customer Service Dashboard', description: 'CS team metrics' },
      { id: 'executive-dashboard', name: 'Executive Dashboard', description: 'High-level company metrics' },
    ];

    dashboards.forEach(dashboard => {
      allResults.push({
        id: `dashboard-${dashboard.id}`,
        title: dashboard.name,
        subtitle: dashboard.description,
        category: 'dashboards',
        icon: LayoutDashboard,
        action: () => {
          if (onNavigate) {
            onNavigate('dashboards', { dashboardId: dashboard.id });
          }
          setIsOpen(false);
        }
      });
    });
  }

  // Profile Settings - ALL users can access
  if (canSearchProfileSettings && visibleCategories.includes('profile')) {
    const profilePages = [
      { id: 'profile', name: 'My Profile', description: 'View and edit your profile information' },
      { id: 'notifications', name: 'Notification Preferences', description: 'Configure email and push notifications' },
      { id: 'security', name: 'Password & Security', description: 'Change password and security settings' },
      { id: 'display', name: 'Display Preferences', description: 'Theme, language, and accessibility settings' },
    ];

    profilePages.forEach(page => {
      allResults.push({
        id: `profile-${page.id}`,
        title: page.name,
        subtitle: page.description,
        category: 'profile',
        icon: UserCircle,
        action: () => {
          if (onNavigate) {
            onNavigate('settings', { section: page.id });
          }
          setIsOpen(false);
        }
      });
    });
  }

  // Clients - search actual client records
  if (canSearchClients && visibleCategories.includes('clients')) {
    const clients = (shouldGobodo && gobodoClients) ? gobodoClients : persistedClientRecords.get();
    clients.slice(0, 50).forEach((client: any) => {
      allResults.push({
        id: `client-${client.id}`,
        title: (client.name ?? client.label ?? (client as any).Name__c) as string,
        subtitle: `${(client as any).industry || 'Client'} • ${(client as any).status || ''}`,
        category: 'clients',
        icon: Building2,
        action: () => {
          if (onNavigate) {
            onNavigate('client-detail', { id: client.id as string });
          }
          setIsOpen(false);
        }
      });
    });
  }

  // Contacts - search actual contact records
  if (canSearchContacts && visibleCategories.includes('contacts')) {
    const contacts = (shouldGobodo && gobodoContacts) ? gobodoContacts : persistedContactRecords.get();
    contacts.slice(0, 50).forEach((contact: any) => {
      allResults.push({
        id: `contact-${contact.id}`,
        title: (contact.name ?? contact.label ?? (contact as any).Name__c) as string,
        subtitle: `${(contact as any).title || ''} ${(contact as any).accountName ? '• ' + (contact as any).accountName : ''}`.trim(),
        category: 'contacts',
        icon: User,
        action: () => {
          if (onNavigate) {
            onNavigate('contact-detail', { id: contact.id as string });
          }
          setIsOpen(false);
        }
      });
    });
  }

  // Leads - search actual lead records
  if (canSearchLeads && visibleCategories.includes('leads')) {
    const leads = (shouldGobodo && gobodoLeads) ? gobodoLeads : persistedLeadRecords.get();
    leads.slice(0, 50).forEach((lead: any) => {
      allResults.push({
        id: `lead-${lead.id}`,
        title: (lead.name ?? lead.label ?? (lead as any).Name__c) as string,
        subtitle: `${(lead as any).company || ''} • ${(lead as any).status || ''}`,
        category: 'leads',
        icon: Users,
        action: () => {
          if (onNavigate) {
            onNavigate('lead-detail', { id: lead.id as string });
          }
          setIsOpen(false);
        }
      });
    });
  }

  // Opportunities - search actual opportunity records
  if (canSearchOpportunities && visibleCategories.includes('opportunities')) {
    const opportunities = (shouldGobodo && gobodoOpportunities) ? gobodoOpportunities : persistedOpportunityRecords.get();
    opportunities.slice(0, 50).forEach((opp: any) => {
      allResults.push({
        id: `opportunity-${opp.id}`,
        title: (opp.name ?? opp.label ?? (opp as any).Name__c) as string,
        subtitle: `${(opp as any).clientName || ''} • ${(opp as any).stage || ''} • $${Number((opp as any).amount || 0).toLocaleString()}`,
        category: 'opportunities',
        icon: TrendingUp,
        action: () => {
          if (onNavigate) {
            onNavigate('opportunity-detail', { id: opp.id as string });
          }
          setIsOpen(false);
        }
      });
    });
  }

  // Filter results based on query
  const filteredResults = query.trim()
    ? allResults.filter(result =>
        result.title.toLowerCase().includes(query.toLowerCase()) ||
        result.subtitle?.toLowerCase().includes(query.toLowerCase())
      )
    : allResults.slice(0, 20); // Show first 20 if no query

  // Group results by category
  const groupedResults = {
    // Records (most commonly searched)
    clients: filteredResults.filter(r => r.category === 'clients'),
    contacts: filteredResults.filter(r => r.category === 'contacts'),
    leads: filteredResults.filter(r => r.category === 'leads'),
    opportunities: filteredResults.filter(r => r.category === 'opportunities'),
    // User settings
    profile: filteredResults.filter(r => r.category === 'profile'),
    // Manager+ features
    reports: filteredResults.filter(r => r.category === 'reports'),
    dashboards: filteredResults.filter(r => r.category === 'dashboards'),
    // Admin features
    objects: filteredResults.filter(r => r.category === 'objects'),
    workflows: filteredResults.filter(r => r.category === 'workflows'),
    fields: filteredResults.filter(r => r.category === 'fields'),
    settings: filteredResults.filter(r => r.category === 'settings')
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filteredResults[selectedIndex]) {
      e.preventDefault();
      filteredResults[selectedIndex].action();
    }
  };

  // Check if user has any search permissions
  const hasAnySearchPermission = visibleCategories.length > 0;

  if (!isOpen) {
    return (
      <button
        onClick={() => hasAnySearchPermission && setIsOpen(true)}
        disabled={!hasAnySearchPermission}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
          hasAnySearchPermission
            ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
            : 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
        }`}
        title={hasAnySearchPermission ? 'Search' : 'Search not available for your role'}
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline">Search</span>
        <kbd className="hidden md:inline-block px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded">
          ⌘K
        </kbd>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-20 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[600px] flex flex-col">
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search objects, workflows, fields, settings..."
              className="flex-1 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 outline-none text-lg"
            />
            <kbd className="hidden sm:inline-block px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
              ESC
            </kbd>
            <button
              onClick={() => {
                setIsOpen(false);
                setQuery('');
                setSelectedIndex(0);
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="overflow-y-auto flex-1 p-2">
          {filteredResults.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">No results found</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                {visibleCategories.length > 0
                  ? `You can search for: ${visibleCategories.join(', ')}`
                  : 'No search access configured for your role'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Clients */}
              {groupedResults.clients.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Clients ({groupedResults.clients.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.clients.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Contacts */}
              {groupedResults.contacts.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Contacts ({groupedResults.contacts.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.contacts.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Leads */}
              {groupedResults.leads.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Leads ({groupedResults.leads.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.leads.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Opportunities */}
              {groupedResults.opportunities.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Opportunities ({groupedResults.opportunities.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.opportunities.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Profile Settings */}
              {groupedResults.profile.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Profile Settings ({groupedResults.profile.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.profile.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Reports */}
              {groupedResults.reports.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Reports ({groupedResults.reports.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.reports.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Dashboards */}
              {groupedResults.dashboards.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Dashboards ({groupedResults.dashboards.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.dashboards.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Objects */}
              {groupedResults.objects.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Objects ({groupedResults.objects.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.objects.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Workflows */}
              {groupedResults.workflows.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Workflows ({groupedResults.workflows.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.workflows.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Fields */}
              {groupedResults.fields.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Fields ({groupedResults.fields.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.fields.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Settings */}
              {groupedResults.settings.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Settings ({groupedResults.settings.length})
                  </div>
                  <div className="space-y-1">
                    {groupedResults.settings.slice(0, 5).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        isSelected={filteredResults.indexOf(result) === selectedIndex}
                        onClick={result.action}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-0.5 bg-white dark:bg-gray-800 rounded">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-0.5 bg-white dark:bg-gray-800 rounded">↵</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-0.5 bg-white dark:bg-gray-800 rounded">ESC</kbd>
                Close
              </span>
            </div>
            <span>{filteredResults.length} results</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Search Result Item Component
const SearchResultItem: React.FC<{
  result: SearchResult;
  isSelected: boolean;
  onClick: () => void;
}> = ({ result, isSelected, onClick }) => {
  const Icon = result.icon;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
        isSelected
          ? 'bg-purple-100 dark:bg-purple-900/20'
          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <div className={`p-2 rounded-lg ${
        isSelected
          ? 'bg-purple-200 dark:bg-purple-800'
          : 'bg-gray-100 dark:bg-gray-700'
      }`}>
        <Icon className={`w-4 h-4 ${
          isSelected
            ? 'text-purple-600 dark:text-purple-400'
            : 'text-gray-600 dark:text-gray-400'
        }`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-medium truncate ${
          isSelected
            ? 'text-purple-900 dark:text-purple-100'
            : 'text-gray-900 dark:text-white'
        }`}>
          {result.title}
        </div>
        {result.subtitle && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {result.subtitle}
          </div>
        )}
      </div>
    </button>
  );
};
